/**
 * ClubARView — AR-style 3D visualization of a full club showing every court
 * arranged like the reference image (covered courts around a central lounge).
 * Uses the existing PadelCourt3D component for each court.
 */
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PadelCourt3D from "@/components/PadelCourt3D";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Cube, Camera, Lightning, Buildings, Users, TennisBall } from "@phosphor-icons/react";
import { useClubTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

export default function ClubARView() {
  const { clubId } = useParams();
  useClubTheme(clubId);
  const [club, setClub] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [xrSupported, setXrSupported] = useState(false);
  const [orbit, setOrbit] = useState({ x: 25, y: 0 });

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.xr) {
      navigator.xr.isSessionSupported("immersive-ar").then((ok) => setXrSupported(!!ok)).catch(() => setXrSupported(false));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/clubs/${clubId}`),
      api.get(`/court-rentals?club_id=${clubId}`).catch(() => ({ data: [] })),
      api.get(`/matches?club_id=${clubId}&status=en_curso`).catch(() => ({ data: [] })),
    ]).then(([c, r, m]) => {
      setClub(c.data);
      setRentals(r.data || []);
      setMatches(m.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [clubId]);

  const launchAR = async () => {
    if (!xrSupported) {
      toast.info("AR no soportado en este navegador. Abre desde Chrome Android o Safari iOS reciente.");
      return;
    }
    try {
      const session = await navigator.xr.requestSession("immersive-ar", { requiredFeatures: ["hit-test"] });
      toast.success(`Sesión AR iniciada · ${club?.name}. Apunta la cámara al piso.`);
      session.addEventListener("end", () => toast("Sesión AR finalizada"));
    } catch {
      toast.error("No se pudo iniciar la sesión AR");
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;
  if (!club) return <div className="card-tactical p-12 text-center"><p className="text-gray-400">Club no encontrado</p></div>;

  const courts = club.court_names && club.court_names.length > 0
    ? club.court_names
    : Array.from({ length: club.courts_count || 4 }, (_, i) => `Cancha ${i + 1}`);
  const courtsCount = courts.length;

  // Distribute courts: half on left, half on right, with central lounge
  const half = Math.ceil(courtsCount / 2);
  const leftCourts = courts.slice(0, half);
  const rightCourts = courts.slice(half);

  const courtActivity = (courtName) => {
    const live = matches.find((m) => m.court_name === courtName);
    const booked = rentals.some((r) => r.court_name === courtName && r.status !== "cancelada");
    if (live) return { status: "live", label: "EN VIVO", color: "#CCFF00" };
    if (booked) return { status: "booked", label: "RESERVADA", color: "#FFB800" };
    return { status: "free", label: "LIBRE", color: "#34C759" };
  };

  return (
    <div className="space-y-5" data-testid="club-ar-view">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to={`/dashboard/clubes/${clubId}`} className="text-[10px] uppercase tracking-widest text-gray-500 hover:text-[#CCFF00] flex items-center gap-1 font-semibold" data-testid="ar-back-club">
          <ArrowLeft size={12} /> Volver al club
        </Link>
        <Badge variant="outline" className="bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px] tracking-widest">
          <Lightning size={11} className="mr-1" weight="fill" /> {xrSupported ? "WebXR activo" : "WebXR no disponible"}
        </Badge>
      </div>

      {/* Hero */}
      <div className="card-tactical p-5 sm:p-6 relative overflow-hidden" data-testid="ar-hero" style={{ borderColor: "rgba(0,160,255,0.4)" }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(0,160,255,0.25) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(204,255,0,0.20) 0%, transparent 70%)" }} />
        </div>
        <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#00A0FF] font-bold mb-1">Club en Realidad Aumentada</p>
            <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-5xl font-black uppercase tracking-tight">{club.name}</h1>
            <p className="text-xs text-gray-400 mt-1">Vista 3D del complejo · {courtsCount} cancha{courtsCount !== 1 ? "s" : ""} · {club.state || ""}{club.country ? `, ${club.country}` : ""}</p>
          </div>
          <div className="flex gap-2 flex-wrap shrink-0">
            <Button onClick={launchAR} className="bg-blue-500 text-white hover:bg-blue-600 font-bold uppercase text-xs h-10 px-4" data-testid="ar-launch-btn">
              <Camera size={14} className="mr-1" weight="duotone" /> Ver en AR
            </Button>
            <Link to={`/dashboard/clubes/${clubId}`}>
              <Button variant="outline" className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 font-bold uppercase text-xs h-10 px-4">
                <Buildings size={14} className="mr-1" /> Detalle Club
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Orbit controls */}
      <div className="card-tactical p-3 flex items-center gap-3 flex-wrap" data-testid="ar-orbit-controls">
        <span className="text-[10px] uppercase tracking-widest text-gray-500">Orbita</span>
        <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-400">
          Inclinación
          <input type="range" min="0" max="45" value={orbit.x} onChange={(e) => setOrbit((o) => ({ ...o, x: +e.target.value }))} className="w-32" data-testid="ar-orbit-x" />
          <span className="font-mono text-white w-6">{orbit.x}°</span>
        </label>
        <label className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-400">
          Rotación
          <input type="range" min="-45" max="45" value={orbit.y} onChange={(e) => setOrbit((o) => ({ ...o, y: +e.target.value }))} className="w-32" data-testid="ar-orbit-y" />
          <span className="font-mono text-white w-6">{orbit.y}°</span>
        </label>
      </div>

      {/* 3D Complex */}
      <div className="card-tactical p-3 sm:p-6 overflow-hidden relative" data-testid="ar-complex">
        {/* sky gradient */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom, #0a0f1c 0%, #1a2545 35%, #0a0f1c 100%)" }} aria-hidden />
        {/* grass ground */}
        <div className="absolute bottom-0 left-0 right-0 h-1/3 opacity-30 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(52,199,89,0.4) 0%, transparent 70%)" }} aria-hidden />

        <div
          className="relative mx-auto"
          style={{
            transform: `perspective(1400px) rotateX(${orbit.x}deg) rotateY(${orbit.y}deg)`,
            transformStyle: "preserve-3d",
            transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            maxWidth: 1100,
          }}
        >
          {/* Left side courts */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            {leftCourts.map((courtName, idx) => {
              const act = courtActivity(courtName);
              return (
                <div key={courtName} className="relative" data-testid={`ar-court-left-${idx}`}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold border" style={{ backgroundColor: `${act.color}20`, color: act.color, borderColor: `${act.color}60` }}>
                    {act.label}
                  </div>
                  <PadelCourt3D name={courtName} size={280} testId={`ar-3d-${idx}`} />
                </div>
              );
            })}
          </div>

          {/* Central building (lounge / bar) */}
          <div className="relative my-4" data-testid="ar-clubhouse">
            <div className="bg-gradient-to-b from-[#1F2937] to-[#0F172A] border-2 border-[#CCFF00]/30 p-6 mx-auto max-w-2xl shadow-2xl"
                 style={{ boxShadow: "0 20px 60px rgba(204,255,0,0.15)" }}>
              <div className="flex items-center justify-center gap-3 mb-2">
                {club.logo_path ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL || ""}${club.logo_path}`} alt={club.name} className="h-12 w-12 object-contain bg-white p-1 rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : null}
                <div className="text-center">
                  <p className="text-[8px] uppercase tracking-[0.3em] text-[#CCFF00] font-bold">Disfruta y Diviértete</p>
                  <p className="font-['Barlow_Condensed'] text-xl sm:text-2xl font-black uppercase">{club.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3 text-center text-[9px] uppercase tracking-widest">
                <div className="bg-black/40 p-2 border border-white/5">
                  <p className="text-gray-500">Canchas</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-black text-[#CCFF00]">{courtsCount}</p>
                </div>
                <div className="bg-black/40 p-2 border border-white/5">
                  <p className="text-gray-500">En vivo</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-black text-green-400">{matches.length}</p>
                </div>
                <div className="bg-black/40 p-2 border border-white/5">
                  <p className="text-gray-500">Reservas</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-black text-orange-400">{rentals.filter((r) => r.status !== "cancelada").length}</p>
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 mt-3 text-[10px] text-gray-400">
                <span>☕ Cafetería</span>
                <span>·</span>
                <span>🛋 Lounge</span>
                <span>·</span>
                <span>🏆 Tienda</span>
              </div>
            </div>
          </div>

          {/* Right side courts */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            {rightCourts.map((courtName, idx) => {
              const act = courtActivity(courtName);
              return (
                <div key={courtName} className="relative" data-testid={`ar-court-right-${idx}`}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-10 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold border" style={{ backgroundColor: `${act.color}20`, color: act.color, borderColor: `${act.color}60` }}>
                    {act.label}
                  </div>
                  <PadelCourt3D name={courtName} size={280} testId={`ar-3d-right-${idx}`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="relative mt-6 grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-widest" data-testid="ar-legend">
          <div className="flex items-center justify-center gap-1.5"><span className="w-2 h-2" style={{ backgroundColor: "#CCFF00" }} /> <span className="text-gray-400">En vivo</span></div>
          <div className="flex items-center justify-center gap-1.5"><span className="w-2 h-2" style={{ backgroundColor: "#FFB800" }} /> <span className="text-gray-400">Reservada</span></div>
          <div className="flex items-center justify-center gap-1.5"><span className="w-2 h-2" style={{ backgroundColor: "#34C759" }} /> <span className="text-gray-400">Libre</span></div>
        </div>
      </div>

      {/* Tip */}
      <div className="card-tactical p-4 flex items-start gap-3" data-testid="ar-tip">
        <Cube size={28} weight="duotone" className="text-[#00A0FF] shrink-0" />
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#00A0FF]">Tip AR</p>
          <p className="text-xs text-gray-400">
            Mueve los sliders de Orbita para girar el complejo y ver cada cancha desde diferentes ángulos. En móviles con <strong>WebXR</strong>, pulsa <strong className="text-blue-300">Ver en AR</strong> para superponer la maqueta de {club.name} sobre cualquier mesa o piso.
          </p>
        </div>
      </div>
    </div>
  );
}
